/**
 * RestoID — Continue (complete your profile).
 * ---------------------------------------------------------------------------
 * One route, six screens: `/continue/:method` for password, username, email, phone,
 * emailOrPhone and extra-profile. RestoID presentation on the PRISTINE upstream logic —
 * the method dispatch, the flow-state guard and every container's decisions are upstream's:
 *   - flow state ............ `continueFlowStateGuard` over `useLocation().state`
 *   - password .............. upstream `usePasswordPolicyChecker`, `fulfillProfile`,
 *                             `usePasswordRejectionErrorHandler`,
 *                             `useSubmitInteractionErrorHandler`, `usePromiseConfirmModal`
 *   - username .............. upstream `useSetUsername` hook, imported as-is
 *   - email / phone ......... upstream `useSendVerificationCode(UserFlow.Continue)`
 *   - extra profile ......... upstream `useSetExtraProfile` hook, imported as-is, over the
 *                             sign-in-experience's `customProfileFields`
 * Swapped in for `./pages/Continue` via `src/App.tsx`.
 *
 * Presentation differences from upstream, all deliberate:
 *   - `SecondaryPageLayout` (NavBar + its own title/description header) is replaced by the
 *     RestoID frame: `FirstScreenLayout` + a single `ScreenHead`. The NavBar's back action
 *     becomes the head's back pill, so every screen keeps `navigate(-1)`.
 *   - the password fields come from `SetPasswordFields`, and the identifier field from
 *     `ContinueIdentifierForm` — RestoID stand-ins for the upstream `@/containers/SetPassword`
 *     and `Continue/IdentifierProfileForm` controls (see those files for the per-control
 *     reasoning).
 *   - the extra-profile fields come from `ContinueProfileFields`, still driven entirely by
 *     the tenant's `customProfileFields` config.
 *   - the head of the extra-profile screen uses the design's own title (`profileTitle`)
 *     over `description.about_yourself`, with upstream's
 *     `description.continue_with_more_information` as the subtitle — the design reference's
 *     ContinueProfileScreen, verbatim.
 *   - `pageContent` and `formSettings` are restated here rather than imported from
 *     `@/pages/Continue/SetEmailOrPhone`, so this page does not pull the upstream screen
 *     (and its whole SecondaryPageLayout tree) into the bundle. They are the same tables.
 *   - upstream's `descriptionProps={{ min, count }}` on the password screen is dropped: its
 *     description was already a rendered element, so those interpolation props were inert.
 */
import { CustomProfileFieldType, MissingProfile, SignInIdentifier } from '@logto/schemas';
import type { FieldPart } from '@logto/schemas';
import type { TFuncKey } from 'i18next';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import { is, validate } from 'superstruct';

import FirstScreenLayout from '@/Layout/FirstScreenLayout';
import PageContext from '@/Providers/PageContextProvider/PageContext';
import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import { fulfillProfile } from '@/apis/experience';
import useApi from '@/hooks/use-api';
import { usePromiseConfirmModal } from '@/hooks/use-confirm-modal';
import type { ErrorHandlers } from '@/hooks/use-error-handler';
import useErrorHandler from '@/hooks/use-error-handler';
import useGlobalRedirectTo from '@/hooks/use-global-redirect-to';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import usePasswordPolicyChecker from '@/hooks/use-password-policy-checker';
import usePasswordRejectionErrorHandler from '@/hooks/use-password-rejection-handler';
import useSendVerificationCode from '@/hooks/use-send-verification-code';
import { usePasswordPolicy, useUsernamePolicyDescription } from '@/hooks/use-sie';
import useSubmitInteractionErrorHandler from '@/hooks/use-submit-interaction-error-handler';
import { useSetExtraProfile } from '@/pages/Continue/SetExtraProfile/use-set-extra-profile';
import useSetUsername from '@/pages/Continue/SetUsername/use-set-username';
import DynamicT from '@/shared/components/DynamicT';
import type { ContinueFlowInteractionEvent, VerificationCodeIdentifier } from '@/types';
import { UserFlow } from '@/types';
import { continueFlowStateGuard, registeredSocialIdentityStateGuard } from '@/types/guard';
import { maskEmail } from '@/utils/format';

import ContinueIdentifierForm from '../components/ContinueIdentifierForm';
import ContinueProfileFields from '../components/ContinueProfileFields';
import SetPasswordFields from '../components/SetPasswordFields';
import { C, FONT, ScreenHead, Stack } from '../components/restoid';
import { useCopy } from '../copy';

import ErrorPage from './ErrorPage';

/** The three missing-profile methods that are fulfilled with a verification code. */
type VerificationCodeProfileType = Exclude<
  MissingProfile,
  'username' | 'password' | 'extraProfile'
>;

type Props = {
  interactionEvent: ContinueFlowInteractionEvent;
};

/** Upstream's `pageContent` table, restated (see the file header). */
const pageContent: Record<VerificationCodeProfileType, { title: TFuncKey; description: TFuncKey }> =
  {
    email: {
      title: 'description.link_email',
      description: 'description.link_email_description',
    },
    phone: {
      title: 'description.link_phone',
      description: 'description.link_phone_description',
    },
    emailOrPhone: {
      title: 'description.link_email_or_phone',
      description: 'description.link_email_or_phone_description',
    },
  };

/** Upstream's `formSettings` table, restated (see the file header). */
const formSettings: Record<
  VerificationCodeProfileType,
  { defaultType: VerificationCodeIdentifier; enabledTypes: VerificationCodeIdentifier[] }
> = {
  email: {
    defaultType: SignInIdentifier.Email,
    enabledTypes: [SignInIdentifier.Email],
  },
  phone: {
    defaultType: SignInIdentifier.Phone,
    enabledTypes: [SignInIdentifier.Phone],
  },
  emailOrPhone: {
    defaultType: SignInIdentifier.Email,
    enabledTypes: [SignInIdentifier.Email, SignInIdentifier.Phone],
  },
};

/**
 * "That email is already linked to another account" — upstream's
 * `SetEmailOrPhone/SocialIdentityNotification`, in RestoID's note type. Same guard, same
 * branch order (email first, then phone, otherwise nothing), same masking.
 */
const SocialIdentityNote = ({
  missingProfileTypes,
}: {
  missingProfileTypes: VerificationCodeIdentifier[];
}) => {
  const { t } = useTranslation();
  const { state } = useLocation();

  const hasSocialIdentity = is(state, registeredSocialIdentityStateGuard);

  if (!hasSocialIdentity) {
    return null;
  }

  const note = (children: string) => (
    <p style={{ margin: 0, textAlign: 'center', color: C.sub, font: `400 13px/1.5 ${FONT}` }}>
      {children}
    </p>
  );

  if (
    missingProfileTypes.includes(SignInIdentifier.Email) &&
    state.registeredSocialIdentity?.email
  ) {
    return note(
      t('description.social_identity_exist', {
        type: t('description.email'),
        value: maskEmail(state.registeredSocialIdentity.email),
      })
    );
  }

  if (
    missingProfileTypes.includes(SignInIdentifier.Phone) &&
    state.registeredSocialIdentity?.phone
  ) {
    return note(
      t('description.social_identity_exist', {
        type: t('description.phone_number'),
        value: state.registeredSocialIdentity.phone,
      })
    );
  }

  return null;
};

/* ---------------------------------------------------------------- password */
const SetPassword = ({ interactionEvent }: Props) => {
  const { t } = useTranslation();
  const c = useCopy();
  const [errorMessage, setErrorMessage] = useState<string>();

  const clearErrorMessage = useCallback(() => {
    setErrorMessage(undefined);
  }, []);

  const navigate = useNavigateWithPreservedSearchParams();
  const { show } = usePromiseConfirmModal();
  const redirectTo = useGlobalRedirectTo();

  const checkPassword = usePasswordPolicyChecker({ setErrorMessage });
  const addPassword = useApi(fulfillProfile);
  const handleError = useErrorHandler();

  const passwordRejectionErrorHandler = usePasswordRejectionErrorHandler({ setErrorMessage });
  const submitInteractionErrorHandler = useSubmitInteractionErrorHandler(interactionEvent, {
    replace: true,
  });

  const errorHandlers: ErrorHandlers = useMemo(
    () => ({
      'user.password_exists_in_profile': async (error) => {
        await show({ type: 'alert', ModalContent: error.message, cancelText: 'action.got_it' });
        navigate(-1);
      },
      ...submitInteractionErrorHandler,
      ...passwordRejectionErrorHandler,
    }),
    [navigate, passwordRejectionErrorHandler, submitInteractionErrorHandler, show]
  );

  const onSubmitHandler = useCallback(
    async (password: string) => {
      const success = await checkPassword(password);

      if (!success) {
        return;
      }

      const [error, result] = await addPassword(
        { type: 'password', value: password },
        interactionEvent
      );

      if (error) {
        await handleError(error, errorHandlers);
        return;
      }

      if (result?.redirectTo) {
        await redirectTo(result.redirectTo);
      }
    },
    [addPassword, checkPassword, errorHandlers, interactionEvent, handleError, redirectTo]
  );

  const {
    policy: {
      length: { max },
    },
    requirementsDescription,
  } = usePasswordPolicy();

  return (
    <FirstScreenLayout pageMeta={{ titleKey: 'description.set_password' }}>
      <Stack gap={22}>
        <ScreenHead
          back={() => navigate(-1)}
          backLabel={c('backShort')}
          title={t('description.set_password')}
          sub={requirementsDescription}
        />
        <SetPasswordFields
          autoFocus
          errorMessage={errorMessage}
          maxLength={max}
          clearErrorMessage={clearErrorMessage}
          onSubmit={onSubmitHandler}
        />
      </Stack>
    </FirstScreenLayout>
  );
};

/* ---------------------------------------------------------------- username */
const SetUsername = ({ interactionEvent }: Props) => {
  const { t } = useTranslation();
  const c = useCopy();
  const navigate = useNavigateWithPreservedSearchParams();
  const { onSubmit, errorMessage, clearErrorMessage } = useSetUsername(interactionEvent);
  const usernamePolicyDescription = useUsernamePolicyDescription();

  const { setIdentifierInputValue } = useContext(UserInteractionContext);

  const handleSubmit = async (identifier: SignInIdentifier, value: string) => {
    if (identifier !== SignInIdentifier.Username) {
      return;
    }

    setIdentifierInputValue({ type: identifier, value });

    return onSubmit(value);
  };

  return (
    <FirstScreenLayout pageMeta={{ titleKey: 'description.enter_username' }}>
      <Stack gap={22}>
        <ScreenHead
          back={() => navigate(-1)}
          backLabel={c('backShort')}
          title={t('description.enter_username')}
          sub={
            usernamePolicyDescription
              ? t('description.enter_username_policy_description', {
                  requirements: usernamePolicyDescription,
                })
              : t('description.enter_username_description')
          }
        />
        <ContinueIdentifierForm
          autoFocus
          errorMessage={errorMessage}
          clearErrorMessage={clearErrorMessage}
          defaultType={SignInIdentifier.Username}
          enabledTypes={[SignInIdentifier.Username]}
          onSubmit={handleSubmit}
        />
      </Stack>
    </FirstScreenLayout>
  );
};

/* ---------------------------------------------------------- email / phone */
const SetEmailOrPhone = ({
  missingProfile,
  interactionEvent,
}: Props & { missingProfile: VerificationCodeProfileType }) => {
  const c = useCopy();
  const navigate = useNavigateWithPreservedSearchParams();
  const { onSubmit, errorMessage, clearErrorMessage } = useSendVerificationCode(UserFlow.Continue);
  const { setIdentifierInputValue } = useContext(UserInteractionContext);

  const handleSubmit = async (identifier: SignInIdentifier, value: string) => {
    // Only handles email and phone
    if (identifier === SignInIdentifier.Username) {
      return;
    }

    setIdentifierInputValue({ type: identifier, value });

    return onSubmit({ identifier, value }, interactionEvent);
  };

  const { title, description } = pageContent[missingProfile];

  return (
    <FirstScreenLayout pageMeta={{ titleKey: title }}>
      <Stack gap={22}>
        <ScreenHead
          back={() => navigate(-1)}
          backLabel={c('backShort')}
          title={<DynamicT forKey={title} />}
          sub={<DynamicT forKey={description} />}
        />
        <ContinueIdentifierForm
          autoFocus
          errorMessage={errorMessage}
          clearErrorMessage={clearErrorMessage}
          {...formSettings[missingProfile]}
          onSubmit={handleSubmit}
        />
        <SocialIdentityNote missingProfileTypes={formSettings[missingProfile].enabledTypes} />
      </Stack>
    </FirstScreenLayout>
  );
};

/* ----------------------------------------------------------- extra profile */
const getDefaultParts = (parts?: FieldPart[]) =>
  Object.fromEntries(parts?.map(({ name, config }) => [name, config?.defaultValue ?? '']) ?? []);

const SetExtraProfile = ({ interactionEvent }: Props) => {
  const { t } = useTranslation();
  const c = useCopy();
  const navigate = useNavigateWithPreservedSearchParams();
  const { experienceSettings } = useContext(PageContext);
  const submit = useSetExtraProfile(interactionEvent);

  const defaultValues = useMemo(() => {
    return experienceSettings?.customProfileFields?.reduce(
      (accumulator, { type, name, config }) => {
        if (type === CustomProfileFieldType.Address) {
          return { ...accumulator, address: getDefaultParts(config.parts) };
        }
        if (type === CustomProfileFieldType.Fullname) {
          return { ...accumulator, ...getDefaultParts(config.parts) };
        }
        return { ...accumulator, [name]: config.defaultValue ?? '' };
      },
      {} satisfies Record<string, unknown>
    );
  }, [experienceSettings?.customProfileFields]);

  return (
    <FirstScreenLayout pageMeta={{ titleKey: 'description.about_yourself' }}>
      <Stack gap={22}>
        <ScreenHead
          back={() => navigate(-1)}
          backLabel={c('backShort')}
          title={c('profileTitle')}
          sub={t('description.continue_with_more_information')}
        />
        <ContinueProfileFields
          customProfileFields={[...(experienceSettings?.customProfileFields ?? [])]}
          defaultValues={defaultValues}
          onSubmit={submit}
        />
      </Stack>
    </FirstScreenLayout>
  );
};

/* -------------------------------------------------------------- the router */
type Parameters = {
  method?: string;
};

const Continue = () => {
  const { method = '' } = useParams<Parameters>();
  const { state } = useLocation();

  const [, continueFlowState] = validate(state, continueFlowStateGuard);

  if (!continueFlowState) {
    return <ErrorPage title="error.invalid_session" rawMessage="flow state not found" />;
  }

  const { interactionEvent } = continueFlowState;

  if (method === MissingProfile.password) {
    return <SetPassword interactionEvent={interactionEvent} />;
  }

  if (method === MissingProfile.username) {
    return <SetUsername interactionEvent={interactionEvent} />;
  }

  if (
    method === MissingProfile.email ||
    method === MissingProfile.phone ||
    method === MissingProfile.emailOrPhone
  ) {
    return <SetEmailOrPhone missingProfile={method} interactionEvent={interactionEvent} />;
  }

  if (method === 'extra-profile') {
    return <SetExtraProfile interactionEvent={interactionEvent} />;
  }

  return <ErrorPage />;
};

export default Continue;
